import { z } from 'zod';
import {
  COLORSET_ID_KEYS,
  ColorSetIdSchema,
  isColorSetId,
  normalizeColorSetId,
} from '../catalog/platformCatalogIds';
import { isOklchColorString } from './colorScience';
import type { ColorSetTokenMap, SelectableColorSetDefinition } from './types';

/** Max project-owned custom palettes (guided cap). */
export const MAX_CUSTOM_COLOR_SETS = 5;

export const CUSTOM_COLORSET_ID_PREFIX = 'custom_' as const;

const oklchColorSchema = z
  .string()
  .refine(isOklchColorString, { message: 'Color must be oklch(L C H)' });

export const ColorSetTokenMapSchema = z.object({
  bgBase: oklchColorSchema,
  bgSurface: oklchColorSchema,
  textMain: oklchColorSchema,
  textMuted: oklchColorSchema,
  brandPrimary: oklchColorSchema,
  brandAccent: oklchColorSchema,
});

export const CustomColorSetIdSchema = z
  .string()
  .regex(
    /^custom_[a-zA-Z0-9_-]{8,64}$/,
    'Custom color-set id must be custom_<id>',
  );

/** Active selection: registry preset id or project custom id. */
export const ProjectColorSetIdSchema = z.preprocess(
  (value) => (typeof value === 'string' ? normalizeColorSetId(value) : value),
  // Plain enum here — outer preprocess already maps legacy ids (avoid nested ZodEffects in union).
  z.union([z.enum(COLORSET_ID_KEYS), CustomColorSetIdSchema]),
);

export type ProjectColorSetId = z.infer<typeof ProjectColorSetIdSchema>;

export const CustomColorSetSchema = z.object({
  id: CustomColorSetIdSchema,
  name: z.string().trim().min(1).max(48),
  /** Optional one-line world brief (chip subtitle), like preset color-world briefs. */
  brief: z.string().trim().max(120).optional(),
  basedOnColorsetId: ColorSetIdSchema.optional(),
  tokens: ColorSetTokenMapSchema,
});

export type CustomColorSet = z.infer<typeof CustomColorSetSchema>;

export const CustomColorSetsSchema = z
  .array(CustomColorSetSchema)
  .max(MAX_CUSTOM_COLOR_SETS);

export function isCustomColorSetId(id: string): boolean {
  return CustomColorSetIdSchema.safeParse(id).success;
}

export function isProjectColorSetId(id: string): boolean {
  return ProjectColorSetIdSchema.safeParse(id).success;
}

/** Registry preset or known custom id on the project. */
export function isSelectableColorSetId(
  themeId: string,
  colorsetId: string,
  customColorSets: readonly CustomColorSet[] | undefined,
  isValidThemeColorSet: (themeId: string, colorsetId: string) => boolean,
): boolean {
  if (isValidThemeColorSet(themeId, colorsetId)) return true;
  if (!isCustomColorSetId(colorsetId)) return false;
  return (customColorSets ?? []).some((set) => set.id === colorsetId);
}

export function findCustomColorSet(
  customColorSets: readonly CustomColorSet[] | undefined,
  colorsetId: string,
): CustomColorSet | undefined {
  if (!isCustomColorSetId(colorsetId)) return undefined;
  return (customColorSets ?? []).find((set) => set.id === colorsetId);
}

export function createCustomColorSetId(): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID().replace(/-/g, '').slice(0, 16)
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  return `${CUSTOM_COLORSET_ID_PREFIX}${id}`;
}

export function customColorSetAsDefinition(
  set: CustomColorSet,
): SelectableColorSetDefinition {
  const brief = set.brief?.trim();
  return {
    id: set.id,
    name: set.name,
    ...(brief ? { brief } : {}),
    tokens: set.tokens,
  };
}

/** Prefer a registry base id when cloning; ignore custom based-on. */
export function resolveBasedOnColorsetId(
  colorsetId: string | undefined,
): z.infer<typeof ColorSetIdSchema> | undefined {
  if (!colorsetId) return undefined;
  const normalized = normalizeColorSetId(colorsetId);
  return isColorSetId(normalized) ? normalized : undefined;
}
